import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useQuery,
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  DefinedInitialDataOptions,
} from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'

import { IPageMeta, TLazyQueryParams } from '@/types'
import { defaultPageMeta } from '@/constants'
import { generatePath } from '@/utils'
import { useToast } from '@/hooks'

import { apiRequest } from './'
import { omit } from 'lodash'

interface TQueryCallbacks<TResponse = unknown> {
  onSuccess?: (_data: TResponse) => void
  onFail?: (_error: any) => void
  onSettled?: () => void
}

interface IQueryOptions<TResponse = unknown>
  extends TQueryCallbacks<TResponse> {
  options?: Partial<
    DefinedInitialDataOptions<AxiosResponse<TResponse> | undefined, AxiosError>
  >
}

interface IPaginatedRes<T = any> {
  pageMeta: {
    totalItems: number
    currentPage: number
    itemsPerPage: number
  }
  data: T[]
}

interface IUsePaginatedRequest {
  enabled?: boolean
  refreshKey?: string
  initialPageMeta?: IPageMeta
  path: string
  params?: TLazyQueryParams<any>
  paginated?: boolean
}

export interface IUsePaginatedRequestResponse<T = any> {
  data: Record<string, T[]>
  total: number
  loading: boolean
  pageMeta: IPageMeta
  onPageMetaChange: (_page: number, _pageSize: number) => void
}

export const onError = (error: any) => {
  if (error.response?.status === 401) {
    // @ts-ignore
    window.global?.signOut?.()
  }
  if (error.response?.status === 403) {
    // @ts-ignore
    window.onUnAuthenticated?.()
  }
}

export const useGet = <TParam = unknown, TResponse = unknown>(
  path: string,
  params?: TParam,
  options?: IQueryOptions<TResponse>,
) => {
  const { addToast } = useToast()
  return useQuery({
    queryKey: [...(options?.options?.queryKey || []), path, params],
    queryFn: async () => {
      try {
        const res = await apiRequest.get<TResponse>(path)
        options?.onSuccess?.(res.data)
        return res
      } catch (error: any) {
        if (error?.message) addToast('error', error.message)
        onError(error)
        options?.onFail?.(error)
      } finally {
        options?.onSettled?.()
      }
    },
    throwOnError: (error: AxiosError) => {
      addToast('error', error.message)
      onError(error)
      return false
    },
    ...options,
  })
}

export const useLazyGet = <TParam = unknown, TResponse = unknown>(
  path: string,
  params?: TLazyQueryParams<TParam>,
  options?: IQueryOptions<TResponse>,
) => {
  const { addToast } = useToast()
  const enabledRef = useRef(false)
  const [enabled, setEnabled] = useState(false)
  const [apiPath, setApiPath] = useState<string>(
    generatePath(path, params?.pathParams || {}, params?.queryParams || []),
  )

  const trigger = useCallback(
    (_payload?: TLazyQueryParams<TParam>) => {
      if (_payload) {
        setApiPath(
          generatePath(
            path,
            _payload?.pathParams || {},
            _payload?.queryParams || [],
          ),
        )
      }
      if (!enabledRef.current) {
        setEnabled(true)
        enabledRef.current = true
      }
    },
    [path],
  )

  const query = useQuery({
    queryKey: [...(options?.options?.queryKey || []), apiPath],
    queryFn: async () => {
      try {
        const res = await apiRequest.get<TResponse>(apiPath)
        options?.onSuccess?.(res.data)
        return res
      } catch (error: any) {
        if (error?.message) addToast('error', error.message)
        onError(error)
        options?.onFail?.(error)
      } finally {
        options?.onSettled?.()
        setEnabled(false)
        enabledRef.current = false
      }
    },
    enabled,
    retry: 0,
    ...options,
  })

  return [trigger, query] as const
}

const defaultData = {}

export const usePaginatedRequest = <T = any>({
  enabled = true,
  refreshKey,
  path,
  params,
  initialPageMeta = defaultPageMeta,
  paginated = true,
}: IUsePaginatedRequest): IUsePaginatedRequestResponse<T> => {
  const { addToast } = useToast()
  const [data, setData] = useState<Record<string, T[]>>(defaultData)
  const [total, setTotal] = useState<number>(0)
  const [pageMeta, setPageMeta] = useState<IPageMeta>(initialPageMeta)
  const [loading, setLoading] = useState<boolean>(false)
  const request = useRef<number>(0)

  const apiPath = useMemo(() => {
    const queryParam = paginated
      ? [
          ...(params?.queryParams || []),
          { page: pageMeta.page },
          { pageSize: pageMeta.pageSize },
        ]
      : params?.queryParams || []
    const pathParam = params?.pathParams || {}
    return generatePath(path, pathParam, queryParam)
  }, [
    pageMeta.page,
    pageMeta.pageSize,
    paginated,
    params?.pathParams,
    params?.queryParams,
    path,
  ])

  useEffect(() => {
    if (refreshKey) {
      setData(defaultData)
      setTotal(0)
    }
  }, [refreshKey])

  useEffect(() => {
    const _request = request.current + 1

    request.current++
    if (!enabled || data?.[pageMeta.page]) return
    setLoading(true)
    apiRequest
      .get<IPaginatedRes<T>>(apiPath)
      .then(res => res.data)
      .then(res => {
        setTotal(res.pageMeta.totalItems ?? 0)
        request.current === _request &&
          setData(prev => {
            const _data = prev[res.pageMeta.currentPage]?.length
              ? {}
              : {
                  [res.pageMeta.currentPage]: res.data,
                }
            return { ...prev, ..._data }
          })
      })
      .catch(error => {
        if (error?.message) addToast('error', error.message)
        onError(error)
      })
      .finally(() => {
        request.current === _request && setLoading(false)
      })
  }, [addToast, apiPath, data, enabled, pageMeta.page])

  const onPageMetaChange = useCallback((_page: number, _pageSize: number) => {
    setPageMeta({
      page: _page,
      pageSize: _pageSize,
    })
  }, [])

  return {
    data,
    total,
    loading,
    pageMeta,
    onPageMetaChange,
  }
}

export function useGenericMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> & {
    hideLoading?: boolean
    notify?: boolean
  },
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { addToast } = useToast()
  return useMutation({
    ...options,
    onError: (error: any, variables, context) => {
      if (error?.message && options.notify) addToast('error', error.message)
      onError(error)
      return options?.onError?.(error, variables, context)
    },
  })
}

export const useDelete = <TParam = unknown, TResponse = unknown>(
  path: string,
  data?: TParam,
  options?: UseMutationOptions<AxiosResponse<TResponse>, AxiosError, TParam>,
) => {
  return useGenericMutation({
    mutationFn: (variables: TParam | void) =>
      apiRequest.delete<TResponse>(
        // @ts-ignore
        generatePath(path, variables?.params ?? {}),
        {
          data: omit(variables || data || {}, 'params'),
        },
      ),
    ...options,
    onError: (error: any) => {
      onError(error)
    },
  })
}

export const usePost = <TParam = unknown, TResponse = unknown>(
  path: string,
  data?: TParam,
  options?: UseMutationOptions<
    AxiosResponse<TResponse>,
    AxiosError,
    TParam | void
  >,
  notify: boolean = true,
) => {
  return useGenericMutation({
    mutationFn: (variables: TParam | void) =>
      apiRequest.post<TResponse>(
        // @ts-ignore
        generatePath(path, variables?.params ?? {}),
        omit(variables || data || {}, 'params'),
      ),
    ...options,
    notify,
    onError: (error: any) => {
      onError(error)
    },
  })
}

export const useUpload = <TParam = unknown, TResponse = unknown>(
  path: string,
  data?: TParam,
  options?: UseMutationOptions<
    AxiosResponse<TResponse>,
    AxiosError,
    TParam | void
  >,
) => {
  return useGenericMutation({
    mutationFn: (variables: TParam | void) => {
      return apiRequest.post<TResponse>(
        // @ts-ignore
        generatePath(path, variables?.params ?? {}),
        omit(variables || data || {}, 'params'),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
    },
    ...options,
    onError: (error: any) => {
      onError(error)
    },
  })
}

export const usePut = <TParam = unknown, TResponse = unknown>(
  path: string,
  data?: TParam,
  options?: UseMutationOptions<
    AxiosResponse<TResponse>,
    AxiosError,
    TParam | void
  >,
) => {
  return useGenericMutation({
    mutationFn: (variables: TParam | void) =>
      apiRequest.put<TResponse>(
        // @ts-ignore
        generatePath(path, variables?.params ?? {}),
        omit(variables || data || {}, 'params'),
      ),
    ...options,
    onError: (error: any) => {
      onError(error)
    },
  })
}
